import { useQuery } from "@tanstack/react-query";
import api from "@/lib/axios";
import {
  SalesTransactionClient,
  SalesTransactionDetail,
  SalesTransactionSearchParams,
  SalesTransactionTotal,
} from "@/interface/settlement/salesTransactionByClient";

// 거래처 목록 조회 (좌측 패널 - Page 응답)
// 매출일자 기간 내 거래가 있는 거래처만 조회
export const useSalesTransactionClients = (
  params: Omit<SalesTransactionSearchParams, "locationSearchType" | "locationSearch"> & { page?: number; size?: number; exactMatch?: boolean }
) => {
  return useQuery<{
    content: SalesTransactionClient[];
    totalElements: number;
    totalPages: number;
    last: boolean;
  }>({
    queryKey: ["salesTransactionClients", params],
    queryFn: async () => {
      const { data } = await api.get("/sale-slip/sales-transaction-by-client/clients", {
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          company: params.company || "",
          searchType: params.searchType || "",
          search: params.search || "",
          exactMatch: params.exactMatch || false,
          page: params.page || 0,
          size: params.size || 20,
        },
      });
      return data.data;
    },
    enabled: !!params.startDate && !!params.endDate,
  });
};

// 선택된 거래처의 거래 내역 조회 (우측 패널)
export const useSalesTransactionDetails = (
  companyCode: string,
  params: SalesTransactionSearchParams & { page?: number; size?: number }
) => {
  return useQuery<{
    content: SalesTransactionDetail[];
    totalElements: number;
    totalPages: number;
    last: boolean;
  }>({
    queryKey: ["salesTransactionDetails", companyCode, params],
    queryFn: async () => {
      const { data } = await api.get(
        `/sale-slip/sales-transaction-by-client/transactions/${companyCode}`,
        {
          params: {
            startDate: params.startDate,
            endDate: params.endDate,
            company: params.company || "",
            locationSearchType: params.locationSearchType || "",
            locationSearch: params.locationSearch || "",
            locationExactMatch: params.locationExactMatch || false,
            page: params.page || 0,
            size: params.size || 20,
          },
        }
      );
      return data.data;
    },
    enabled: !!companyCode && !!params.startDate && !!params.endDate,
  });
};

// 선택된 거래처의 거래 합계 조회 (Footer용)
export const useSalesTransactionTotals = (
  companyCode: string,
  params: SalesTransactionSearchParams
) => {
  return useQuery<SalesTransactionTotal>({
    queryKey: ["salesTransactionTotals", companyCode, params],
    queryFn: async () => {
      const { data } = await api.get(
        `/sale-slip/sales-transaction-by-client/transactions/${companyCode}/totals`,
        {
          params: {
            startDate: params.startDate,
            endDate: params.endDate,
            company: params.company || "",
            locationSearchType: params.locationSearchType || "",
            locationSearch: params.locationSearch || "",
            locationExactMatch: params.locationExactMatch || false,
          },
        }
      );
      return data.data;
    },
    enabled: !!companyCode && !!params.startDate && !!params.endDate,
  });
};

// 선택된 거래처의 전체 거래 내역 조회 (PDF 출력용 - 페이지네이션 없이 전체 데이터)
export const fetchAllSalesTransactionDetails = async (
  companyCode: string,
  params: SalesTransactionSearchParams
): Promise<SalesTransactionDetail[]> => {
  const { data } = await api.get(
    `/sale-slip/sales-transaction-by-client/transactions/${companyCode}/all`,
    {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
        company: params.company || "",
        locationSearchType: params.locationSearchType || "",
        locationSearch: params.locationSearch || "",
        locationExactMatch: params.locationExactMatch || false,
      },
    }
  );
  return data.data;
};
